/**
 * Notification & message sounds for taskeri
 * Web Audio API — programmatic, reliable, no base64 dependencies.
 *
 * Current active style can be swapped by changing the exports at the bottom.
 * All styles are exported individually for the /sound-test page.
 */

let _audioCtx = null;

function ctx() {
  // AudioContext must be created & resumed inside a user gesture (tryUnlock).
  // Once running, it stays running and can play sounds from any context (WebSocket, timers).
  if (!_audioCtx || _audioCtx.state === 'closed') return null;
  // Browsers (esp. Chrome/mobile) auto-suspend the context when the tab is
  // backgrounded or after a period of inactivity. A notification/message sound
  // triggered via WebSocket then silently fails. Resume best-effort — oscillators
  // scheduled at currentTime still fire once the context flips back to running.
  if (_audioCtx.state === 'suspended') {
    _audioCtx.resume().catch(() => {});
  }
  return _audioCtx;
}

// Create & warm up AudioContext on first real user gesture.
// Once running, it persists for background sounds (notifications, messages via WebSocket).
function tryUnlock() {
  if (_audioCtx && _audioCtx.state === 'running') return;
  try {
    if (!_audioCtx || _audioCtx.state === 'closed') {
      _audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (_audioCtx.state === 'suspended') {
      _audioCtx.resume();
    }
  } catch (err) {
    // AudioContext creation can fail on older browsers / iOS lock screen — benign
    if (typeof window !== 'undefined' && window.__AUDIO_DEBUG) console.debug('[sounds] AudioContext unlock failed:', err);
  }
}
['click', 'touchstart', 'keydown', 'mousedown', 'pointerdown'].forEach(evt =>
  document.addEventListener(evt, tryUnlock, { once: false, passive: true })
);

const _lastPlayed = {};
const DEBOUNCE_MS = 1500;
function debounce(key) {
  const now = Date.now();
  if (_lastPlayed[key] && now - _lastPlayed[key] < DEBOUNCE_MS) return false;
  _lastPlayed[key] = now;
  return true;
}

// ─── STYLE A: Soft & Minimal (iMessage-like bubbles) ────────────────────────

export function softNotif() {
  if (!debounce('softNotif')) return;
  try {
    const c = ctx(); if (!c) return;
    const t = c.currentTime;

    // Drop 1 — high pop
    const g1 = c.createGain();
    g1.connect(c.destination);
    g1.gain.setValueAtTime(0.85, t);
    g1.gain.exponentialRampToValueAtTime(0.001, t + 0.15);

    const o1 = c.createOscillator();
    o1.type = 'sine';
    o1.frequency.setValueAtTime(1400, t);
    o1.frequency.exponentialRampToValueAtTime(900, t + 0.12);
    o1.connect(g1);
    o1.start(t);
    o1.stop(t + 0.15);

    // Drop 2 — lower, slightly delayed
    const g2 = c.createGain();
    g2.connect(c.destination);
    g2.gain.setValueAtTime(0, t);
    g2.gain.setValueAtTime(0.7, t + 0.1);
    g2.gain.exponentialRampToValueAtTime(0.001, t + 0.3);

    const o2 = c.createOscillator();
    o2.type = 'sine';
    o2.frequency.setValueAtTime(1100, t + 0.1);
    o2.frequency.exponentialRampToValueAtTime(700, t + 0.25);
    o2.connect(g2);
    o2.start(t + 0.1);
    o2.stop(t + 0.3);
  } catch (e) { console.warn('Sound error:', e); }
}

export function softMsg() {
  if (!debounce('softMsg')) return;
  try {
    const c = ctx(); if (!c) return;
    const t = c.currentTime;

    const g = c.createGain();
    g.connect(c.destination);
    g.gain.setValueAtTime(0.8, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.18);

    const o = c.createOscillator();
    o.type = 'sine';
    o.frequency.setValueAtTime(1200, t);
    o.frequency.exponentialRampToValueAtTime(800, t + 0.15);
    o.connect(g);
    o.start(t);
    o.stop(t + 0.18);
  } catch (e) { console.warn('Sound error:', e); }
}

// ─── STYLE B: Crisp & Techy (Slack/Discord-like) ────────────────────────────

export function crispNotif() {
  if (!debounce('crispNotif')) return;
  try {
    const c = ctx(); if (!c) return;
    const t = c.currentTime;

    // Chirp 1
    const g1 = c.createGain();
    g1.connect(c.destination);
    g1.gain.setValueAtTime(0.8, t);
    g1.gain.exponentialRampToValueAtTime(0.001, t + 0.12);

    const o1 = c.createOscillator();
    o1.type = 'triangle';
    o1.frequency.setValueAtTime(880, t);
    o1.frequency.setValueAtTime(1047, t + 0.04);
    o1.connect(g1);
    o1.start(t);
    o1.stop(t + 0.12);

    // Chirp 2 — higher, offset
    const g2 = c.createGain();
    g2.connect(c.destination);
    g2.gain.setValueAtTime(0, t);
    g2.gain.setValueAtTime(0.75, t + 0.09);
    g2.gain.exponentialRampToValueAtTime(0.001, t + 0.22);

    const o2 = c.createOscillator();
    o2.type = 'triangle';
    o2.frequency.setValueAtTime(1175, t + 0.09);
    o2.frequency.setValueAtTime(1319, t + 0.13);
    o2.connect(g2);
    o2.start(t + 0.09);
    o2.stop(t + 0.22);

    // Chirp 3 — highest, completing the triad
    const g3 = c.createGain();
    g3.connect(c.destination);
    g3.gain.setValueAtTime(0, t);
    g3.gain.setValueAtTime(0.6, t + 0.17);
    g3.gain.exponentialRampToValueAtTime(0.001, t + 0.3);

    const o3 = c.createOscillator();
    o3.type = 'triangle';
    o3.frequency.setValueAtTime(1568, t + 0.17);
    o3.connect(g3);
    o3.start(t + 0.17);
    o3.stop(t + 0.3);
  } catch (e) { console.warn('Sound error:', e); }
}

export function crispMsg() {
  if (!debounce('crispMsg')) return;
  try {
    const c = ctx(); if (!c) return;
    const t = c.currentTime;

    const g = c.createGain();
    g.connect(c.destination);
    g.gain.setValueAtTime(0.8, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.15);

    const o = c.createOscillator();
    o.type = 'triangle';
    o.frequency.setValueAtTime(1047, t);
    o.frequency.setValueAtTime(1319, t + 0.05);
    o.connect(g);
    o.start(t);
    o.stop(t + 0.15);
  } catch (e) { console.warn('Sound error:', e); }
}

// ─── STYLE C: Warm & Musical (piano-like with overtones) ─────────────────────

export function warmNotif() {
  if (!debounce('warmNotif')) return;
  try {
    const c = ctx(); if (!c) return;
    const t = c.currentTime;

    // Fundamental — C5
    const g1 = c.createGain();
    g1.connect(c.destination);
    g1.gain.setValueAtTime(0.7, t);
    g1.gain.exponentialRampToValueAtTime(0.3, t + 0.15);
    g1.gain.exponentialRampToValueAtTime(0.001, t + 0.5);

    const o1 = c.createOscillator();
    o1.type = 'sine';
    o1.frequency.setValueAtTime(523, t);
    o1.connect(g1);
    o1.start(t);
    o1.stop(t + 0.5);

    // 2nd harmonic — adds warmth
    const g1h = c.createGain();
    g1h.connect(c.destination);
    g1h.gain.setValueAtTime(0.25, t);
    g1h.gain.exponentialRampToValueAtTime(0.001, t + 0.35);

    const o1h = c.createOscillator();
    o1h.type = 'sine';
    o1h.frequency.setValueAtTime(1046, t);
    o1h.connect(g1h);
    o1h.start(t);
    o1h.stop(t + 0.35);

    // Second note — E5, delayed
    const g2 = c.createGain();
    g2.connect(c.destination);
    g2.gain.setValueAtTime(0, t);
    g2.gain.setValueAtTime(0.65, t + 0.15);
    g2.gain.exponentialRampToValueAtTime(0.25, t + 0.3);
    g2.gain.exponentialRampToValueAtTime(0.001, t + 0.6);

    const o2 = c.createOscillator();
    o2.type = 'sine';
    o2.frequency.setValueAtTime(659, t + 0.15);
    o2.connect(g2);
    o2.start(t + 0.15);
    o2.stop(t + 0.6);

    // Third note — G5
    const g3 = c.createGain();
    g3.connect(c.destination);
    g3.gain.setValueAtTime(0, t);
    g3.gain.setValueAtTime(0.55, t + 0.3);
    g3.gain.exponentialRampToValueAtTime(0.001, t + 0.65);

    const o3 = c.createOscillator();
    o3.type = 'sine';
    o3.frequency.setValueAtTime(784, t + 0.3);
    o3.connect(g3);
    o3.start(t + 0.3);
    o3.stop(t + 0.65);
  } catch (e) { console.warn('Sound error:', e); }
}

export function warmMsg() {
  if (!debounce('warmMsg')) return;
  try {
    const c = ctx(); if (!c) return;
    const t = c.currentTime;

    // Single warm tone — E5 with harmonic
    const g = c.createGain();
    g.connect(c.destination);
    g.gain.setValueAtTime(0.7, t);
    g.gain.exponentialRampToValueAtTime(0.2, t + 0.12);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.4);

    const o = c.createOscillator();
    o.type = 'sine';
    o.frequency.setValueAtTime(659, t);
    o.connect(g);
    o.start(t);
    o.stop(t + 0.4);

    // Harmonic
    const gh = c.createGain();
    gh.connect(c.destination);
    gh.gain.setValueAtTime(0.2, t);
    gh.gain.exponentialRampToValueAtTime(0.001, t + 0.25);

    const oh = c.createOscillator();
    oh.type = 'sine';
    oh.frequency.setValueAtTime(1318, t);
    oh.connect(gh);
    oh.start(t);
    oh.stop(t + 0.25);
  } catch (e) { console.warn('Sound error:', e); }
}

// ─── ACTIVE SOUNDS (change these to swap app-wide style) ─────────────────────
// Default: Crisp & Techy. Change to softNotif/softMsg or warmNotif/warmMsg.

export const playNotificationSound = warmNotif;
export const playMessageSound = warmMsg;
